import { useEffect } from "react";

/**
 * Google Tag Manager, loaded first-party through our own wrapper on
 * gss.spectaclehq.com (server container GTM-NMNDZZB4, web container
 * GTM-NW5ZWW72) — the same container and the same loader the marketing site
 * uses, so a visitor who signs up carries one Ga4 session across from
 * www.spectaclehq.com. Both hosts sit under spectaclehq.com and the `_ga` and
 * `FPID` cookies are written to that registrable domain, so nothing has to be
 * linked by hand.
 *
 * Mounted inside `AuthFlow`, which renders only while `authStatus` is
 * `unauthenticated`. That is deliberate and is the whole safety property here:
 * every tag in GTM-NW5ZWW72 fires on All Pages, so loading the container
 * anywhere inside the signed-in product would start Microsoft Clarity, Hubspot
 * and Tapfiliate on top of customers' own analytics data. Scoping by trigger
 * conditions would leave that one forgotten checkbox away; never loading the
 * container for an authenticated user cannot regress that way.
 *
 * Kept byte-for-byte in step with `marketing-website-new/custom-code/gtm.js`.
 * The wrapper loads the container itself and pushes its own `gtm.start`, so
 * neither appears here — doing either would start GTM twice.
 */

const WRAPPER_SRC = "https://gss.spectaclehq.com/whmmgpjjsi.js";

// Where `onerror` falls back to: the container straight from our tagging
// server, the url the server container's Web Container client claims. That is
// server-side tracking without the cloaking, so a wrapper that never arrives
// costs uplift measurement rather than all tagging.
const FALLBACK_SRC = "https://gss.spectaclehq.com/gtm.js?id=GTM-NW5ZWW72";

declare global {
    interface Window {
        dataLayer?: unknown[];
    }
}

let injected = false;

export default function GoogleTagManagerScript() {
    useEffect(() => {
        if (!import.meta.env.PROD || injected) {
            return;
        }
        injected = true;

        const first = document.getElementsByTagName("script")[0];
        if (!first?.parentNode) {
            return;
        }

        const wrapper = document.createElement("script");
        wrapper.async = true;
        wrapper.src = WRAPPER_SRC;
        wrapper.onerror = () => {
            const fallback = document.createElement("script");
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                "gtm.start": new Date().getTime(),
                event: "gtm.js",
            });
            fallback.async = true;
            fallback.src = FALLBACK_SRC;
            first.parentNode?.insertBefore(fallback, first);
        };
        first.parentNode.insertBefore(wrapper, first);
    }, []);

    return null;
}
